import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi, type Org } from '@/lib/api';
import { JOIN_POLICY_LABELS } from '@/lib/labels';
import { Badge, Button, Card, ErrorText, Field, Input, Select } from '@/components/ui';
import { useOrgContext } from './layout';

export function OrgSettingsPage() {
  const { org, canManage } = useOrgContext();
  if (!canManage) return <Navigate to=".." replace />;
  return (
    <div className="space-y-6">
      <Domains org={org} />
      <Details org={org} />
    </div>
  );
}

function Domains({ org }: { org: Org }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [domain, setDomain] = useState('');
  const domains = useQuery({ queryKey: ['domains', org.id], queryFn: () => api.domains(org.id) });
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['domains', org.id] });
    queryClient.invalidateQueries({ queryKey: ['usage'] });
  };
  const add = useMutation({
    mutationFn: () => api.addDomain(org.id, domain),
    onSuccess: () => {
      setDomain('');
      refresh();
    },
  });
  const verify = useMutation({
    mutationFn: (domainId: string) => api.verifyDomain(org.id, domainId),
    onSuccess: refresh,
  });
  const policy = useMutation({
    mutationFn: (joinPolicy: Org['joinPolicy']) => api.updateOrg(org.id, { joinPolicy }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org', org.id] }),
  });

  return (
    <Card title="Email domains">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Verify your email domain and people who sign in with it can join {org.name} without an
          invite code.
        </p>
        {domains.data?.domains.map((d) => (
          <div key={d.id} className="rounded-lg border border-border p-3 text-sm">
            <div className="flex items-center justify-between gap-3">
              <span className="font-medium">{d.domain}</span>
              {d.verifiedAt ? (
                <Badge>Verified</Badge>
              ) : (
                <Button
                  variant="secondary"
                  disabled={verify.isPending}
                  onClick={() => verify.mutate(d.id)}
                >
                  Check DNS
                </Button>
              )}
            </div>
            {!d.verifiedAt && (
              <p className="mt-2 break-all text-muted-foreground">
                Add a TXT record <code className="font-mono">{d.record.name}</code> with the value{' '}
                <code className="font-mono">{d.record.value}</code>, then select Check DNS.
              </p>
            )}
          </div>
        ))}
        {verify.data && !verify.data.verified && (
          <p className="text-sm text-muted-foreground">
            Record not found yet. DNS changes can take a few minutes.
          </p>
        )}
        <form
          className="flex gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            add.mutate();
          }}
        >
          <Input
            aria-label="Domain"
            placeholder="university.edu"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            required
          />
          <Button type="submit" disabled={add.isPending}>
            Add domain
          </Button>
        </form>
        <Field label="When someone signs in with a verified domain" htmlFor="join-policy">
          <Select
            id="join-policy"
            value={org.joinPolicy}
            onChange={(e) => policy.mutate(e.target.value as Org['joinPolicy'])}
          >
            {Object.entries(JOIN_POLICY_LABELS).map(([k, label]) => (
              <option key={k} value={k} disabled={k === 'managed'}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <ErrorText error={add.error ?? verify.error ?? policy.error} />
      </div>
    </Card>
  );
}

function Details({ org }: { org: Org }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [name, setName] = useState(org.name);
  const save = useMutation({
    mutationFn: () => api.updateOrg(org.id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org', org.id] });
      queryClient.invalidateQueries({ queryKey: ['myOrgs'] });
    },
  });
  return (
    <Card title="Details">
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <div className="min-w-64 flex-1">
          <Field label="Organization name" htmlFor="org-name">
            <Input
              id="org-name"
              required
              minLength={2}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Field>
        </div>
        <Button type="submit" variant="secondary" disabled={save.isPending || name === org.name}>
          Save
        </Button>
      </form>
      <ErrorText error={save.error} />
    </Card>
  );
}
